// Shared keyboard-shortcut plumbing for the transaction-form stack.
//
// One implementation drives the same shortcuts on every surface that mounts
// these components: the designer live preview, the in-app Run view, and the
// downloaded standalone runtime. Each surface wires the same callbacks (Add /
// Edit / Save / Delete / paging / search / lookup), so binding them here keeps
// the behaviour identical everywhere instead of re-implementing per host.
//
// Supported combos (case-insensitive, Cmd treated as Ctrl on macOS):
//   F3        Add            F4    Edit          Ctrl+Delete  Delete
//   Ctrl+S    Save           Ctrl+N Next page    Ctrl+P       Previous page
//   Ctrl+E    Expand toggle  F2    Show lookup   Ctrl+F       Search
//   ArrowUp / ArrowDown / Enter   (lookup-modal navigation)

import { useCallback, useEffect, useRef } from 'react'
import type { RefObject } from 'react'

export interface Shortcut {
  // Normalised combo or list of combos, e.g. 'f3', 'ctrl+s', 'ctrl+delete',
  // 'ctrl+n', 'ctrl+e', 'ctrl+f', 'f2', 'arrowdown', 'enter'. Modifier order
  // is fixed: ctrl+alt+shift+<key>. Cmd (metaKey) is folded into 'ctrl'.
  combo: string | string[]
  run: (e: KeyboardEvent) => void
  // Default true — also fire while focus is in a text input / textarea /
  // select / contenteditable. Every shortcut here is a function key or a
  // modifier combo, which never conflicts with plain typing, so the default
  // is safe. Set false for bare keys that would clobber typing.
  allowInInput?: boolean
  // Default true — call preventDefault() so the browser's own binding
  // (Ctrl+S save-page, Ctrl+F find, Ctrl+P print, Ctrl+E, F-keys, arrow
  // scrolling) doesn't also run. Note: some browsers reserve Ctrl+N (new
  // window) at the OS/chrome level and won't surface it to the page.
  preventDefault?: boolean
}

// Build the canonical combo string for a keyboard event.
export function comboOf(e: KeyboardEvent): string {
  const parts: string[] = []
  if (e.ctrlKey || e.metaKey) parts.push('ctrl')
  if (e.altKey) parts.push('alt')
  if (e.shiftKey) parts.push('shift')
  let key = (e.key || '').toLowerCase()
  if (key === ' ' || key === 'spacebar') key = 'space'
  parts.push(key)
  return parts.join('+')
}

const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

function isTypingTarget(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  if (el.isContentEditable) return true
  return TYPING_TAGS.has(el.tagName)
}

interface Options {
  // When false the listener is removed entirely (e.g. a list disables its
  // shortcuts while its record editor is open so the editor's own bindings
  // take over).
  enabled?: boolean
  // When set, shortcuts only fire while the user is pointing at, or focused
  // within, this element. This scopes a list's / form's shortcuts to that
  // region so several lists or forms on one page (smart-page embeds) don't
  // all respond to the same key press.
  scopeRef?: RefObject<HTMLElement | null>
}

export function useKeyboardShortcuts(shortcuts: Shortcut[], opts: Options = {}): void {
  const { enabled = true, scopeRef } = opts
  // Keep the latest handlers in a ref so changing callbacks don't re-bind the
  // window listener every render.
  const ref = useRef(shortcuts)
  ref.current = shortcuts

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      const scope = scopeRef?.current
      if (scope) {
        const active = document.activeElement
        const focusedInside = active instanceof Node && scope.contains(active)
        let hoveredInside = false
        try {
          hoveredInside = typeof scope.matches === 'function' && scope.matches(':hover')
        } catch {
          hoveredInside = false
        }
        if (!focusedInside && !hoveredInside) return
      }
      const combo = comboOf(e)
      for (const s of ref.current) {
        const combos = Array.isArray(s.combo) ? s.combo : [s.combo]
        if (!combos.includes(combo)) continue
        if (s.allowInInput === false && isTypingTarget()) continue
        if (s.preventDefault !== false) e.preventDefault()
        try {
          s.run(e)
        } catch {
          // A shortcut handler throwing must never break the page; the
          // underlying action surfaces its own errors via the host's notifier.
        }
        break
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // scopeRef is a stable ref object; enabled toggles re-bind.
  }, [enabled, scopeRef])
}

// ── Modal layering ────────────────────────────────────────────────────────────
// Stacked overlays (record editor → child-row editor → lookup picker) are
// DOM-nested, so an outer card's scopeRef still "contains" focus that really
// lives in an inner overlay. For an *exclusive* shortcut — Ctrl+S save — that
// means an outer editor would fire alongside the inner one (saving the header
// AND the child row). A layer registry fixes this: every open overlay registers
// while mounted, and `isTopLayer()` is true only for the most-recently-opened
// one, so the save shortcut fires on exactly the overlay the user is in.
const openLayers: number[] = []
let layerCounter = 0

export function useShortcutLayer(active = true): { isTopLayer: () => boolean } {
  const idRef = useRef(0)
  if (idRef.current === 0) idRef.current = ++layerCounter
  useEffect(() => {
    if (!active) return
    const id = idRef.current
    openLayers.push(id)
    return () => {
      const i = openLayers.indexOf(id)
      if (i >= 0) openLayers.splice(i, 1)
    }
  }, [active])
  const isTopLayer = useCallback(
    () => openLayers.length === 0 || openLayers[openLayers.length - 1] === idRef.current,
    [],
  )
  return { isTopLayer }
}
